import { ApolloQueryResult, useMutation } from '@apollo/client';
import { Case, MutationResponse, VALID_FILE_TYPES } from '@bybeam/platform-types';
import Loading from '@platform-ui-common/components/Loading/index.js';
import { validateFileName } from '@platform-ui-common/components/Upload/utils/validateFilename.js';
import useSnackbar from '@platform-ui-common/hooks/useSnackbar/index.js';
import Color, { TextColors } from '@platform-ui-common/utilities/Color.js';
import { TemporaryNewDocument } from '@platform-ui/@types/document.js';
import cx from 'classnames';
import { ChangeEvent, ReactNode } from 'react';
import sanitize from 'sanitize-filename';
import styles from './CaseDocumentUpload.module.css';
import UploadCaseDocumentMutation from './UploadCaseDocumentMutation.graphql';

interface CaseDocumentUploadProps {
  children: ReactNode;
  caseId: string;
  refetchData?: () => Promise<
    ApolloQueryResult<{
      cases: {
        cases: Case[];
      };
    }>
  >;
  multiple?: boolean;
}

export default function CaseDocumentUpload({
  caseId,
  children,
  multiple = true,
  refetchData,
}: CaseDocumentUploadProps): JSX.Element {
  const { showSnackbar } = useSnackbar();

  const [uploadDocument, { loading: uploadLoading }] = useMutation<
    { case: { uploadDocuments: MutationResponse<Case> } },
    { caseId: string; files: File[] }
  >(UploadCaseDocumentMutation);

  const handleUpload = async (documents: TemporaryNewDocument[]): Promise<void> => {
    const files = documents.map(({ file }) => file);
    const {
      data: {
        case: {
          uploadDocuments: { metadata },
        },
      },
    } = await uploadDocument({ variables: { caseId, files } });

    if (metadata.status < 400) {
      refetchData();
    } else {
      showSnackbar('Failed to upload document. Please refresh and try again');
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (!e.target.files || e.target.files.length === 0) return;

    const rawDocuments = e.target.files;
    if (rawDocuments.length > 100) {
      <>
        <strong>Document upload failed:</strong> You can upload a max of 100 documents at a time.
        Please try again.
      </>;
      return;
    }

    const filenamesHaveError = validateFileName(rawDocuments);
    if (filenamesHaveError) {
      <>
        <strong>Document upload failed:</strong> {filenamesHaveError}
      </>;
      return;
    }
    const formattedDocuments: TemporaryNewDocument[] = Array.from(rawDocuments).map((file) => ({
      file,
      url: `${URL.createObjectURL(file)}`,
      name: sanitize(file.name),
      mimetype: file.type,
      isNew: true,
    }));
    handleUpload(formattedDocuments);
    e.target.value = null;
  };
  const acceptedTypes = VALID_FILE_TYPES.map((type) => `.${type}`).join(',');

  if (uploadLoading) {
    return (
      <div>
        <Loading size="MD" />
      </div>
    );
  }

  return (
    <label
      htmlFor={`upload-${caseId}`}
      className={cx(
        'inline-flex min-w-min gap-x-2 items-center justify-center cursor-pointer leading-none font-semibold uppercase whitespace-nowrap text-primary py-2 px-4',
        'focus-within:outline focus-within:outline-2 focus-within:outline-firefoxDefaultFocus focus-within:outline-webkitDefaultFocus',
        TextColors[Color.Primary],
      )}
    >
      <input
        id={`upload-${caseId}`}
        type="file"
        accept={acceptedTypes}
        className={cx(styles.CaseDocumentUpload, 'max-w-0 w-0 h-0')}
        multiple={multiple}
        onChange={onChange}
      />
      {children}
    </label>
  );
}
